<script setup lang="ts">
defineProps<{
  fullScreen?: boolean
}>()

defineEmits<{
  close: []
}>()

const preferenceKey = 'docue_agent_private_beta_requested'

const { t } = useI18n()
const { activeWorkspaceId } = useAuth()
const { notify } = useBugsnag()
const { mutateAsync: sendContactRequest, isPending: isSending, isError } = useMutationHubspot.sendContactRequest()
const { mutateAsync: upsertPreference, isPending: isSaving } = useMutationPreferences.upsertByKey()
const { data: preference, isLoading } = useQueryPreferences.getByKey({
  key: preferenceKey,
  useQueryOptions: { retry: false },
})

const isRequestSent = ref(false)
const hasRequested = computed(() => isRequestSent.value || !!preference.value?.data?.attributes.value)
const isPending = computed(() => isSending.value || isSaving.value)

async function requestAccess() {
  const workspaceId = activeWorkspaceId.value
  if (!workspaceId || isPending.value || hasRequested.value)
    return

  try {
    await sendContactRequest({
      payload: {
        message: 'Docue Agent early access request: I would like to join the beta. Please contact me when you can invite me to early access.',
      },
    })
    isRequestSent.value = true
    await upsertPreference({
      workspaceId,
      payload: { key: preferenceKey, value: 1 },
    })
  }
  catch (error) {
    if (error instanceof Error)
      notify(error)
  }
}
</script>

<template>
  <div
    class="flex flex-col bg-white"
    :class="fullScreen ? 'h-full' : 'w-[min(28rem,calc(100vw-1.5rem))] rounded-xl border border-gray-200 shadow-lg'"
  >
    <div class="flex shrink-0 items-center gap-2 px-4 py-3">
      <h2
        class="min-w-0 font-medium text-gray-900"
        :class="fullScreen ? 'text-base' : 'text-sm'"
      >
        {{ t('docueAgent.title') }}
      </h2>
      <UiBadge
        color="info"
        variant="soft"
        aspect="rounded"
      >
        {{ t('docueAgent.beta') }}
      </UiBadge>
      <UiButton
        type="button"
        variant="ghost"
        :size="fullScreen ? 'lg' : 'xs'"
        aspect-square
        icon="docue:x-close"
        class="ml-auto"
        :aria-label="t('close')"
        @click="$emit('close')"
      />
    </div>
    <div
      class="flex flex-col items-center px-7 pb-8 pt-6 text-center"
      :class="{ 'my-auto': fullScreen }"
      aria-live="polite"
    >
      <UiSpinner
        v-if="isLoading"
        class="size-5 text-gray-400"
      />
      <template v-else>
        <div
          class="
            mb-6 flex size-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-700
            ring-1 ring-gray-200
          "
        >
          <UiIcon
            name="docue:stars-01"
            class="size-7"
          />
        </div>
        <p class="text-xl font-semibold leading-tight text-gray-900">
          {{ t(hasRequested ? 'docueAgent.privateBeta.sentTitle' : 'docueAgent.privateBeta.title') }}
        </p>
        <p
          v-if="hasRequested"
          class="mt-4 text-sm leading-6 text-gray-600"
        >
          {{ t('docueAgent.privateBeta.sentDescription') }}
        </p>
        <p
          v-else
          class="mt-4 text-sm leading-6 text-gray-600"
        >
          {{ t('docueAgent.privateBeta.description') }}
        </p>
        <p
          v-if="!hasRequested"
          class="mt-4 text-sm leading-6 text-gray-700"
        >
          {{ t('docueAgent.privateBeta.invitation') }}
        </p>
        <ErrorAlert
          v-if="!hasRequested"
          :error="isError ? t('docueAgent.privateBeta.error') : undefined"
          class="mt-4 w-full text-left"
        />
        <UiButton
          v-if="!hasRequested"
          type="button"
          color="primary"
          class="mt-6"
          :disabled="!activeWorkspaceId || isPending"
          :loading="isPending"
          @click="requestAccess"
        >
          {{ t('docueAgent.privateBeta.cta') }}
        </UiButton>
        <p
          v-if="!hasRequested"
          class="mt-3 text-xs leading-5 text-gray-500"
        >
          {{ t('docueAgent.privateBeta.followUp') }}
        </p>
      </template>
    </div>
  </div>
</template>
